
import { v4 as uuidv4 } from "uuid";
import { 
  IStringNumHash,
  Feature,
  FeatureUserRating,
  FloatingFeature,
  FeatureBase,
} from "@models";
import { 
  Character,
  DamageMultiplier,
  UpgradableNumber,
  TemplateBase,
  AbilityScoresUpgradable,
  Attributes,
  SKILLS,
  MinionAbility,
  CreatureAbility,
  Ability,
} from "@dndModels";

import CommonUtilities from "@utilities/common_utilities";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import { CreatureType, DamageType, isCreatureType, isDamageType, SizeType } from "@dndModels/Types";
import { HIT_DICE_SIZE_MAP, SIZE_TO_NUM_MAP } from "@dndModels/Constants";

const common_util = CommonUtilities.getInstance();
const char_util = DandDCharacterUtilities.getInstance();

export class Summon {
  true_id = "";
  name = "";
  description = "";
  connected = false;
  image_url = "";
  // home
  use_your_pb = true;
  challenge_rating: UpgradableNumber = new UpgradableNumber();
  creature_type: CreatureType = "None"; // Beast, Dragon, Humanoid, etc.
  subtypes: string[] = []; // Drow, Bronze, etc.
  tags: string[] = [];
  swarm: boolean = false;
  hd_size_from_size: boolean = false;
  hit_dice_size: UpgradableNumber = new UpgradableNumber();
  hit_dice_count: UpgradableNumber = new UpgradableNumber();
  max_hit_points: UpgradableNumber = new UpgradableNumber();
  calc_max_hp: boolean = false;
  temp_hit_points: UpgradableNumber = new UpgradableNumber();
  share_initiative: boolean = true;
  initiative_modifier: UpgradableNumber = new UpgradableNumber();
  armor_class: UpgradableNumber = new UpgradableNumber();
  size_num: SizeType = 3;
  alignment = "";
  ability_scores: AbilityScoresUpgradable = new AbilityScoresUpgradable();
  fbs: FeatureBase[] = [];
  get action_fbs() {
    console.log(this.fbs);
    return this.fbs.filter(o => o.creature_fb_type === "Actions" && o.single_ff && 
      (o.single_ff.feature_type === "Extra Attacks" || o.single_ff.feature_type === "Minion Extra Attacks" ||
      (o.single_ff instanceof MinionAbility || 
        o.single_ff instanceof CreatureAbility || 
        o.single_ff instanceof Ability) 
      // && (o.single_ff.casting_time !== "BA" && o.single_ff.casting_time !== "RA")
      )
    );
  }
  get actions() {
    return this.action_fbs.flatMap(o => o.features);
  }
  get ba_fbs() {
    return this.fbs.filter(o => o.creature_fb_type === "Actions" && o.single_ff && 
      (o.single_ff instanceof MinionAbility || o.single_ff instanceof CreatureAbility || o.single_ff instanceof Ability) && 
      (o.single_ff.casting_time === "BA")
    );
  }
  get bonus_actions() {
    return this.ba_fbs.flatMap(o => o.features);
  }
  get reaction_fbs() {
    return this.fbs.filter(o => o.creature_fb_type === "Actions" && o.single_ff && 
      (o.single_ff instanceof MinionAbility || o.single_ff instanceof CreatureAbility || o.single_ff instanceof Ability) && 
      (o.single_ff.casting_time === "RA")
    );
  }
  get reactions() {
    return this.reaction_fbs.flatMap(o => o.features);
  }
  get legendary_action_fbs() {
    return this.fbs.filter(o => o.creature_fb_type === "Legendary Actions");
  }
  get legendary_actions() {
    return this.legendary_action_fbs.flatMap(o => o.features);
  }
  get trait_fbs() {
    return this.fbs.filter(o => o.creature_fb_type === "Traits");
  }
  get traits() {
    return this.trait_fbs.flatMap(o => o.features);
  }
  built_in_gift_ids: string[] = [];

  // attributes
  attributes: Attributes = new Attributes();
  get speed() {
    return this.attributes.speeds;
  }
  get senses() {
    return this.attributes.senses;
  }
  // These are now automatically calculated
  // passives: {
  //   passive_perception: UpgradableNumber,
  //   passive_insight: UpgradableNumber,
  //   passive_investigation: UpgradableNumber
  // } = {
  //   passive_perception: new UpgradableNumber(),
  //   passive_insight: new UpgradableNumber(),
  //   passive_investigation: new UpgradableNumber()
  // };
  get condition_immunities() {
    return this.attributes.condition_immunities;
  }
  // skill_proficiencies_upgradable: { _id: string, name: string, value: UpgradableNumber }[] = [];
  // saving_throws: AbilityScoresUpgradable = new AbilityScoresUpgradable()
  get languages() {
    return this.attributes.languages;
  }
  get flying() {
    const fly = this.speed.fly;
    if (fly) {
      return true;
    }
    return false;
  }
  get swimming() {
    const swim = this.speed.swim;
    if (swim) {
      return true;
    }
    return false;
  }

  constructor(obj?: any, log_it = false) {
    this.true_id = obj && obj.true_id ? obj.true_id : uuidv4().toString();
    if (obj) {
      this.name = obj.name ? obj.name : "";
      this.description = obj.description ? obj.description : "";
      (this.creature_type as string) = obj.creature_type ? (obj.creature_type ? common_util.capitalize_firsts(obj.creature_type) : common_util.capitalize_firsts(obj.type)) : "";
      if (!isCreatureType(this.creature_type)) {
        this.creature_type = "None";
      }
      this.subtypes = obj.subtypes ? obj.subtypes : [];
      this.swarm = obj.swarm ? obj.swarm : false;
      if (this.subtypes.length === 0 && obj.subtype) {
        const subtype = obj.subtype ? common_util.capitalize_firsts(obj.subtype) : "";
        if (subtype !== "") {
          this.subtypes.push(subtype);
        }
      }
      this.tags = obj.tags ? obj.tags : [];
      this.use_your_pb = obj.use_your_pb !== undefined ? obj.use_your_pb : true;
      this.challenge_rating = obj.challenge_rating ? new UpgradableNumber(obj.challenge_rating) : new UpgradableNumber();
      this.image_url = obj.image_url ? obj.image_url : "";
      this.ability_scores = obj.ability_scores ? new AbilityScoresUpgradable(obj.ability_scores) : new AbilityScoresUpgradable();
      
      if (obj.fbs) {
        this.fbs = obj.fbs.map((o: any) => new FeatureBase(o));
      } else {
        const actions: Feature[] = [];
        if (obj.actions && obj.actions.length > 0) {
          for (const o of obj.actions) {
            const feature = new Feature(o);
            feature.parent_type = "summon";
            feature.parent_id = this.true_id;
            feature.base_id = this.true_id;
            actions.push(feature);
          }
        }
        // this.legendary_actions = [];
        if (obj.legendary_actions && obj.legendary_actions.length > 0) {
          debugger;
          // for (const o of obj.legendary_actions) {
          //   const feature = new Feature(o);
          //   feature.parent_type = "summon";
          //   feature.parent_id = this.true_id;
          //   feature.base_id = this.true_id;
          //   this.legendary_actions.push(feature);
          // }
        }
        const special_abilities: Feature[] = [];
        if (obj.special_abilities && obj.special_abilities.length > 0) {
          for (const o of obj.special_abilities) {
            const feature = new Feature(o);
            feature.parent_type = "summon";
            feature.parent_id = this.true_id;
            feature.base_id = this.true_id;
            special_abilities.push(feature);
          }
        }
        for (const f of actions) {
          const fb = new FeatureBase();
          fb.creature_fb_type = "Actions";
          f.base_id = fb.true_id;
          fb.features.push(f);
          this.fbs.push(fb);
        }
        for (const f of special_abilities) {
          const fb = new FeatureBase();
          fb.creature_fb_type = "Traits";
          f.base_id = fb.true_id;
          fb.features.push(f);
          this.fbs.push(fb);
        }
      }
      this.hd_size_from_size = obj.hd_size_from_size ? obj.hd_size_from_size : false;
      this.hit_dice_size = obj.hit_dice_size ? new UpgradableNumber(obj.hit_dice_size) : new UpgradableNumber();
      this.hit_dice_count = obj.hit_dice_count ? new UpgradableNumber(obj.hit_dice_count) : new UpgradableNumber();
      if (log_it) {
        debugger;
      }
      this.max_hit_points = obj.max_hit_points ? new UpgradableNumber(obj.max_hit_points) : new UpgradableNumber();
      this.calc_max_hp = obj.calc_max_hp ? obj.calc_max_hp : false;
      this.temp_hit_points = obj.temp_hit_points ? new UpgradableNumber(obj.temp_hit_points) : new UpgradableNumber();
      this.attributes = obj.attributes ? new Attributes(obj.attributes) : new Attributes();
      if (!obj.attributes) {
        if (obj.speed) {
          const speed = obj.speed;
          const hover = typeof speed.hover === "number" && speed.hover > 0;
          delete speed.hover;
          this.attributes.speeds = speed;
          this.attributes.hover = hover;
        }
        if (obj.senses) {
          if (obj.senses.length > 0) { 
            // Older ones might have been stored as an array.
            // Probably unnecessary to check for this. I think they've all been converted.
            // for (const sense of obj.senses) {
            //   // console.log(sense);
            //   this.senses[sense.name] = sense.range;
            //   // this.senses.push(new SenseFeature(sense));
            // }
            debugger;
          } else {
            this.attributes.senses = obj.senses;
          }
        }
        if (obj.condition_immunities) {
          this.attributes.condition_immunities = obj.condition_immunities ? obj.condition_immunities : [];
        }
        if (obj.damage_multipliers && obj.damage_multipliers.length > 0) {
          for (const dm of obj.damage_multipliers) {
            let damage_types: string[] = dm.the_damage_types ? dm.the_damage_types : (dm.damage_types ? dm.damage_types : []);
            const multiplier: number = dm.multiplier ? dm.multiplier : 0;
            const the_damage_types = damage_types.map(o => {
              const dt = o[0].toLocaleUpperCase() + o.substring(1);
              if (isDamageType(dt)) {
                return dt;
              }
              return "";
            }).filter(o => o !== "") as (DamageType)[];
            switch (multiplier) {
              case 0: this.attributes.damage_immunities.push(...the_damage_types); break;
              case 0.5: this.attributes.damage_resistances.push(...the_damage_types); break;
              case 2: this.attributes.damage_vulnerabilities.push(...the_damage_types); break;
            }
          }
        }
        if (obj.skill_proficiencies_upgradable) {
          for (const upg of obj.skill_proficiencies_upgradable) {
            const skill = SKILLS.find(o => o.name === upg.name);
            if (skill) {
              // I don't think there are any summons with expertise, so I'm not going to worry about that.
              this.attributes.skill_profs[skill._id] = 1;
            }
          }
        } else if (obj.skill_proficiencies) {
          for (const name of Object.keys(obj.skill_proficiencies)) {
            const skill = SKILLS.find(o => o.name === name);
            if (skill) {
              // I don't think there are any summons with expertise, so I'm not going to worry about that.
              this.attributes.skill_profs[skill._id] = 1;
            }
          }
        }
        if (obj.saving_throws) {
          // console.log(obj.saving_throws);
          if (obj.saving_throws.strength) {
            const upg = new UpgradableNumber(obj.saving_throws.strength);
            if (!upg.isBasic()) {
              this.attributes.save_profs.push("STR");
            } else if (upg.base > 8) {
              this.attributes.save_profs.push("STR");
            }
          } else {
            debugger;
          }
          if (obj.saving_throws.dexterity) {
            const upg = new UpgradableNumber(obj.saving_throws.dexterity);
            if (!upg.isBasic()) {
              this.attributes.save_profs.push("DEX");
            } else if (upg.base > 8) {
              this.attributes.save_profs.push("DEX");
            }
          }
          if (obj.saving_throws.constitution) {
            const upg = new UpgradableNumber(obj.saving_throws.constitution);
            if (!upg.isBasic()) {
              this.attributes.save_profs.push("CON");
            } else if (upg.base > 8) {
              this.attributes.save_profs.push("CON");
            }
          }
          if (obj.saving_throws.intelligence) {
            const upg = new UpgradableNumber(obj.saving_throws.intelligence);
            if (!upg.isBasic()) {
              this.attributes.save_profs.push("INT");
            } else if (upg.base > 8) {
              this.attributes.save_profs.push("INT");
            }
          }
          if (obj.saving_throws.wisdom) {
            const upg = new UpgradableNumber(obj.saving_throws.wisdom);
            if (!upg.isBasic()) {
              this.attributes.save_profs.push("WIS");
            } else if (upg.base > 8) {
              this.attributes.save_profs.push("WIS");
            }
          }
          if (obj.saving_throws.charisma) {
            const upg = new UpgradableNumber(obj.saving_throws.charisma);
            if (!upg.isBasic()) {
              this.attributes.save_profs.push("CHA");
            } else if (upg.base > 8) {
              this.attributes.save_profs.push("CHA");
            }
          }
        }
        if (typeof obj.languages === "string") {
          this.attributes.languages = obj.languages;
        }
      }
      if (log_it) {
        debugger;
      }
      this.initiative_modifier = obj.initiative_modifier ? new UpgradableNumber(obj.initiative_modifier) : new UpgradableNumber();
      this.share_initiative = obj.share_initiative !== undefined ? obj.share_initiative : true;
      if (obj.share_initiative === undefined && !this.initiative_modifier.isBlank()) {
        this.share_initiative = false;
      }
      this.armor_class = obj.armor_class ? new UpgradableNumber(obj.armor_class) : new UpgradableNumber();
      this.size_num = obj.size_num ? obj.size_num : (obj.size ? SIZE_TO_NUM_MAP[obj.size] : 3);
      this.alignment = obj.alignment ? common_util.capitalize_firsts(obj.alignment) : "Neutral";
      this.built_in_gift_ids = obj.built_in_gift_ids ? obj.built_in_gift_ids : [];
      this.connected = false;
      if (log_it) {
        debugger;
      }
    }
  }

  toDBObj(): any {
    return {
      true_id: this.true_id,
      name: this.name,
      description: this.description,
      creature_type: this.creature_type,
      subtypes: this.subtypes,
      tags: this.tags,
      swarm: this.swarm,
      image_url: this.image_url,
      ability_scores: this.ability_scores.toDBObj(),
      hd_size_from_size: this.hd_size_from_size,
      hit_dice_size: this.hit_dice_size.toDBObj(),
      hit_dice_count: this.hit_dice_count.toDBObj(),
      fbs: this.fbs.map(o => o.toDBObj()),
      // actions: this.actions.map(o => o.toDBObj()),
      // legendary_actions: this.legendary_actions.map(o => o.toDBObj()),
      // special_abilities: this.special_abilities.map(o => o.toDBObj()),
      use_your_pb: this.use_your_pb,
      challenge_rating: this.challenge_rating.toDBObj(),
      max_hit_points: this.max_hit_points.toDBObj(),
      calc_max_hp: this.calc_max_hp,
      temp_hit_points: this.temp_hit_points.toDBObj(),
      attributes: this.attributes.toDBObj(),
      share_initiative: this.share_initiative,
      initiative_modifier: this.initiative_modifier.toDBObj(),
      armor_class: this.armor_class.toDBObj(),
      size_num: this.size_num,
      alignment: this.alignment,
      built_in_gift_ids: this.built_in_gift_ids,
    };
  }

  hit_dice_string(char: Character) {
    const size = this.hd_size_from_size ? HIT_DICE_SIZE_MAP[this.size_num] : char_util.value(this.hit_dice_size,{ obj: char, item_id: "" });
    const response = `${char_util.value(this.hit_dice_count, { obj: char, item_id: "" })}d${size}`;
    return response;
  }

  get skills_string() {
    return this.attributes.skills_string;
  }

  get tools_string() {
    return this.attributes.tools_string;
  }

  get senses_string() {
    return this.attributes.senses_string;
  }

  get saving_throws_string() {
    return this.attributes.saving_throws_string;
  }

  get speed_string() {
    return this.attributes.speed_string;
  }

  get immunities_string() {
    return this.attributes.immunities_string;
  }

  get resistances_string() {
    return this.attributes.resistances_string;
  }

  get vulnerabilities_string() {
    return this.attributes.vulnerabilities_string;
  }

  get condition_immunities_string() {
    return this.attributes.condition_immunities_string;
  }

  // getDTsForMultiplier(multiplier: number) {
  //   let dts = this.getDTsForMultiplierSimple(multiplier);
  //   if (multiplier === 0) {
  //     dts.push(...this.getUpgradedDTs());
  //   } else if (multiplier === 0.5) {
  //     const immune = this.getDTsForMultiplier(0);
  //     dts = dts.filter(dt => !immune.includes(dt));
  //   } 
  //   return common_util.unique_array(dts);
  // }

  // getDTsForMultiplierSimple(multiplier: number) {
  //   return common_util.unique_array(this.damage_multipliers.filter(o => o.multiplier === multiplier).flatMap(o => o.damage_types));
  // }

  // getUpgradedDTs() {
  //   const upgraded: DamageType[] = [];
  //   const res_dms = this.damage_multipliers.filter(o => o.multiplier === 0.5 && o.multiplier_improvement);
  //   for (const dm of res_dms) {
  //     // Look for another dm for the same type which is also resistance.
  //     for (const dt of dm.damage_types) {
  //       const other_dm = this.damage_multipliers.find(o => o.damage_types.includes(dt) && o._id !== dm._id && o.multiplier === 0.5);
  //       if (other_dm && !upgraded.includes(dt)) {
  //         upgraded.push(dt);
  //       }
  //     }
  //   }
  //   return upgraded;
  // }

  copy(copyMe: Summon, drop_ids = false): void {
    if (!drop_ids) {
      this.true_id = copyMe.true_id;
    }
    this.name = copyMe.name;
    this.description = copyMe.description;
    this.creature_type = copyMe.creature_type;
    this.subtypes = [...copyMe.subtypes];
    this.tags = [...copyMe.tags];
    this.swarm = copyMe.swarm;
    this.connected = copyMe.connected;
    this.ability_scores = copyMe.ability_scores.clone();
    this.image_url = copyMe.image_url;
    this.hd_size_from_size = copyMe.hd_size_from_size;
    this.hit_dice_size = copyMe.hit_dice_size.clone();
    this.hit_dice_count = copyMe.hit_dice_count.clone();
    this.use_your_pb = copyMe.use_your_pb;
    this.challenge_rating = copyMe.challenge_rating.clone();
    this.max_hit_points = copyMe.max_hit_points.clone();
    this.calc_max_hp = copyMe.calc_max_hp;
    this.temp_hit_points = copyMe.temp_hit_points.clone();
    this.attributes = copyMe.attributes.clone();
    this.share_initiative = copyMe.share_initiative;
    this.initiative_modifier = copyMe.initiative_modifier.clone();
    this.armor_class = copyMe.armor_class.clone();
    this.fbs = copyMe.fbs.map(o => o.clone());
    // this.actions = copyMe.actions.map(o => o.clone(drop_ids));
    // this.legendary_actions = copyMe.legendary_actions.map(o => o.clone(drop_ids));
    // this.special_abilities = copyMe.special_abilities.map(o => o.clone(drop_ids));
    this.size_num = copyMe.size_num;
    this.alignment = copyMe.alignment;
    this.built_in_gift_ids = [...copyMe.built_in_gift_ids];
  }

  clone(drop_ids = false): Summon {
    const new_obj = new Summon();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }

  copyTemplate(copyMe: TemplateBase, all_floating_features: FloatingFeature[]): void {
    this.name = copyMe.name;
    this.description = copyMe.description;
    const obj = {...copyMe.item};
    (this.creature_type as string) = obj ? (obj.creature_type ? common_util.capitalize_firsts(obj.creature_type) : common_util.capitalize_firsts(obj.type)) : "";
    if (!isCreatureType(this.creature_type)) {
      this.creature_type = "None";
    }
    this.subtypes = obj && obj.subtypes ? obj.subtypes : [];
    this.swarm = obj && obj.swarm ? obj.swarm : false;
    if (this.subtypes.length === 0 && obj && obj.subtype) {
      const subtype = obj ? common_util.capitalize_firsts(obj.subtype) : "";
      if (subtype !== "") {
        this.subtypes.push(subtype);
      }
    }
    if (obj && obj.tags) {
      this.tags = obj.tags;
    }
    this.use_your_pb = obj && obj.use_your_pb !== undefined ? obj.use_your_pb : true;
    this.challenge_rating = obj ? new UpgradableNumber(obj.challenge_rating) : new UpgradableNumber();
    this.image_url = obj && obj.image_url ? obj.image_url : "";
    this.ability_scores = obj ? new AbilityScoresUpgradable(obj.ability_scores) : new AbilityScoresUpgradable();
    if (obj) {
      if (obj.fbs) {
        this.fbs = obj.fbs.map((o: any) => new FeatureBase(o));
      } else {
        const actions: Feature[] = [];
        if (obj.actions && obj.actions.length > 0) {
          for (const o of obj.actions) {
            const feature = new Feature(o);
            feature.parent_type = "summon";
            feature.parent_id = this.true_id;
            feature.base_id = this.true_id;
            actions.push(feature);
          }
        }
        // this.legendary_actions = [];
        if (obj.legendary_actions && obj.legendary_actions.length > 0) {
          debugger;
          // for (const o of obj.legendary_actions) {
          //   const feature = new Feature(o);
          //   feature.parent_type = "summon";
          //   feature.parent_id = this.true_id;
          //   feature.base_id = this.true_id;
          //   this.legendary_actions.push(feature);
          // }
        }
        const special_abilities: Feature[] = [];
        if (obj.special_abilities && obj.special_abilities.length > 0) {
          for (const o of obj.special_abilities) {
            const feature = new Feature(o);
            feature.parent_type = "summon";
            feature.parent_id = this.true_id;
            feature.base_id = this.true_id;
            special_abilities.push(feature);
          }
        }
        for (const f of actions) {
          const fb = new FeatureBase();
          fb.creature_fb_type = "Actions";
          fb.features.push(f);
          this.fbs.push(fb);
        }
        for (const f of special_abilities) {
          const fb = new FeatureBase();
          fb.creature_fb_type = "Traits";
          fb.features.push(f);
          this.fbs.push(fb);
        }
      }
    }
    this.hd_size_from_size = obj && obj.hd_size_from_size ? obj.hd_size_from_size : false;
    this.hit_dice_size = obj ? new UpgradableNumber(obj.hit_dice_size) : new UpgradableNumber();
    this.hit_dice_count = obj ? new UpgradableNumber(obj.hit_dice_count) : new UpgradableNumber();
    this.max_hit_points = obj ? new UpgradableNumber(obj.max_hit_points) : new UpgradableNumber();
    this.calc_max_hp = obj && obj.calc_max_hp ? obj.calc_max_hp : false;
    this.temp_hit_points = obj.temp_hit_points ? new UpgradableNumber(obj.temp_hit_points) : new UpgradableNumber();
    this.attributes = obj && obj.attributes ? new Attributes(obj.attributes) : new Attributes();
    if (obj && !obj.attributes) {
      if (obj.speed) {
        const speed = obj.speed;
        const hover = typeof speed.hover === "number" && speed.hover > 0;
        delete speed.hover;
        this.attributes.speeds = speed;
        this.attributes.hover = hover;
      }
      if (obj.senses) {
        if (obj.senses.length > 0) {
          // Older ones might have been stored as an array.
          // Probably unnecessary to check for this. I think they've all been converted.
          // for (const sense of obj.senses) {
          //   // console.log(sense);
          //   this.senses[sense.name] = sense.range;
          //   // this.senses.push(new SenseFeature(sense));
          // }
          debugger;
        } else {
          this.attributes.senses = { ...obj.senses };
        }
      }
      if (obj.condition_immunities) {
        this.attributes.condition_immunities = obj.condition_immunities ? obj.condition_immunities : [];
      }
      if (obj.damage_multipliers && obj.damage_multipliers.length > 0) {
        for (const dm of obj.damage_multipliers) {
          let damage_types: string[] = dm.the_damage_types ? dm.the_damage_types : (dm.damage_types ? dm.damage_types : []);
          const multiplier: number = dm.multiplier ? dm.multiplier : 0;
          const the_damage_types = damage_types.map(o => {
            const dt = o[0].toLocaleUpperCase() + o.substring(1);
            if (isDamageType(dt)) {
              return dt;
            }
            return "";
          }).filter(o => o !== "") as (DamageType)[];
          switch (multiplier) {
            case 0: this.attributes.damage_immunities.push(...the_damage_types); break;
            case 0.5: this.attributes.damage_resistances.push(...the_damage_types); break;
            case 2: this.attributes.damage_vulnerabilities.push(...the_damage_types); break;
          }
        }
      }
      if (obj.skill_proficiencies_upgradable) {
        for (const upg of obj.skill_proficiencies_upgradable) {
          const skill = SKILLS.find(o => o.name === upg.name);
          if (skill) {
            // I don't think there are any summons with expertise, so I'm not going to worry about that.
            this.attributes.skill_profs[skill._id] = 1;
          }
        }
      } else if (obj.skill_proficiencies) {
        for (const name of Object.keys(obj.skill_proficiencies)) {
          const skill = SKILLS.find(o => o.name === name);
          if (skill) {
            // I don't think there are any summons with expertise, so I'm not going to worry about that.
            this.attributes.skill_profs[skill._id] = 1;
          }
        }
      }
      if (obj.saving_throws) {
        if (obj.saving_throws.strength) {
          const upg = new UpgradableNumber(obj.saving_throws.strength);
          if (!upg.isBasic()) {
            this.attributes.save_profs.push("STR");
          } else if (upg.base > 8) {
            this.attributes.save_profs.push("STR");
          }
        } else {
          debugger;
        }
        if (obj.saving_throws.dexterity) {
          const upg = new UpgradableNumber(obj.saving_throws.dexterity);
          if (!upg.isBasic()) {
            this.attributes.save_profs.push("DEX");
          } else if (upg.base > 8) {
            this.attributes.save_profs.push("DEX");
          }
        }
        if (obj.saving_throws.constitution) {
          const upg = new UpgradableNumber(obj.saving_throws.constitution);
          if (!upg.isBasic()) {
            this.attributes.save_profs.push("CON");
          } else if (upg.base > 8) {
            this.attributes.save_profs.push("CON");
          }
        }
        if (obj.saving_throws.intelligence) {
          const upg = new UpgradableNumber(obj.saving_throws.intelligence);
          if (!upg.isBasic()) {
            this.attributes.save_profs.push("INT");
          } else if (upg.base > 8) {
            this.attributes.save_profs.push("INT");
          }
        }
        if (obj.saving_throws.wisdom) {
          const upg = new UpgradableNumber(obj.saving_throws.wisdom);
          if (!upg.isBasic()) {
            this.attributes.save_profs.push("WIS");
          } else if (upg.base > 8) {
            this.attributes.save_profs.push("WIS");
          }
        }
        if (obj.saving_throws.charisma) {
          const upg = new UpgradableNumber(obj.saving_throws.charisma);
          if (!upg.isBasic()) {
            this.attributes.save_profs.push("CHA");
          } else if (upg.base > 8) {
            this.attributes.save_profs.push("CHA");
          }
        }
      }
      if (typeof obj.languages === "string") {
        this.attributes.languages = obj.languages;
      }
    }
    this.share_initiative = obj && obj.share_initiative !== undefined ? obj.share_initiative : true;
    this.initiative_modifier = obj ? new UpgradableNumber(obj.initiative_modifier) : new UpgradableNumber();
    if (obj && obj.share_initiative === undefined && !this.initiative_modifier.isBlank()) {
      this.share_initiative = false;
    }
    this.armor_class = obj ? new UpgradableNumber(obj.armor_class) : new UpgradableNumber();
    this.size_num = obj && obj.size_num ? obj.size_num : (obj && obj.size ? SIZE_TO_NUM_MAP[obj.size] : 3);
    this.alignment = obj ? common_util.capitalize_firsts(obj.alignment) : "Neutral";
    this.built_in_gift_ids = obj && obj.built_in_gift_ids ? obj.built_in_gift_ids : [];
    this.connected = false;
  }

  connectUserRatings(userRatings: FeatureUserRating[]) {
    // this.actions.forEach(feature => {
    //   feature.connectUserRatings(userRatings);
    // });
    // this.legendary_actions.forEach(feature => {
    //   feature.connectUserRatings(userRatings);
    // });
    // this.special_abilities.forEach(feature => {
    //   feature.connectUserRatings(userRatings);
    // });
  }
}

